/**
 * FolderShareModal
 *
 * E035.4: Modal for managing folder permissions.
 * Shows visibility, effective access, direct overrides, and advanced inheritance.
 * Fully i18n-compatible.
 */
import {
  Button,
  ConfirmModal,
  Loading,
  notify,
  RowInput,
} from '@affine/component';
import type {
  FolderRole,
  GrantedFolderUser,
  GroupData,
} from '@affine/core/modules/folder-api';
import type { Member } from '@affine/core/modules/permissions';
import {
  endObjectConfigurationTrace,
  recordObjectConfigurationTraceEvent,
} from '@affine/core/modules/permissions/utils/object-configuration-trace';
import {
  getFolderWorkspaceRoleCeilingNotice,
  getObjectPermissionAreaBlockedNotice as getAreaBlockedNotice,
  isFolderGroupRolePartiallyLimited,
  useObjectPermissionOnlineOnlyState,
} from '@affine/core/modules/permissions/utils/object-permission-ui';
import { resolvePermissionPeoplePresentation } from '@affine/core/modules/permissions/utils/permission-people-presentation';
import { ObjectPermissionDialogShell } from '@affine/core/modules/permissions/views/object-permission-dialog-shell';
import * as accessStyles from '@affine/core/modules/permissions/views/permission-access-list.css';
import {
  PermissionGroupInviteSearch,
  PermissionGroupRow,
  PermissionMemberInviteSearch,
  PermissionMemberRow,
} from '@affine/core/modules/permissions/views/permission-invite-picker';
import { PermissionReviewList } from '@affine/core/modules/permissions/views/permission-review-list';
import { FolderRole as FolderRoleEnum } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { FolderGroupRoleWithName } from '../hooks/use-folder-share';
import { useFolderShare } from '../hooks/use-folder-share';
import {
  FOLDER_DIRECT_ASSIGNABLE_ROLES,
  FolderPermissionAdvancedSettings,
  folderPermissionDialogHasAdvancedSettings,
  type FolderPermissionDialogVariant,
  FolderPermissionExpandableSection,
  FolderPermissionRoleSelector,
  type FolderPermissionVisibility,
  getFolderPermissionDialogPolicy,
  matchesFolderPermissionQuery,
  resolveFolderPermissionVisibility,
  useFolderPermissionInviteCopy,
  useObjectRoleLabel,
  useObjectRoleOptions,
} from './folder-permission-core';
import { FolderShareMatesSection } from './folder-share-mates-section';
import * as styles from './folder-share-modal.css';

export interface FolderShareModalProps {
  folderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: FolderPermissionDialogVariant;
}

// --- Sub-Components ---

interface UserRowProps {
  user: GrantedFolderUser;
  canManage: boolean;
  onChangeRole: (userId: string, role: FolderRole) => void;
  onResetRole: (userId: string) => void;
  onRemove: (userId: string) => void;
  getRoleLabel: (role: string) => string;
}

const UserRow = ({
  user,
  canManage,
  onChangeRole,
  onResetRole,
  onRemove,
  getRoleLabel,
}: UserRowProps) => {
  const t = useI18n();
  const isOwner = user.role === FolderRoleEnum.Owner;
  const areaBlockedNotice = getAreaBlockedNotice(t, user);
  const workspaceCeilingNotice =
    user.membershipKind === 'house_guests'
      ? null
      : getFolderWorkspaceRoleCeilingNotice(
          t,
          user.workspaceRole,
          FOLDER_DIRECT_ASSIGNABLE_ROLES
        );

  return (
    <div
      className={`${accessStyles.accessRowFrame} ${
        user.isAreaBlocked ? accessStyles.areaBlockedAccessRow : ''
      }`}
      data-area-blocked={user.isAreaBlocked ? 'true' : undefined}
      data-folder-access-inherited={user.isInherited ? 'true' : 'false'}
      data-folder-access-direct={user.isDirectOverride ? 'true' : 'false'}
    >
      <PermissionMemberRow
        dataTestId={`folder-share-user-row-${user.user.id}`}
        user={{
          id: user.user.id,
          name: user.user.name,
          email: user.user.email,
          avatarUrl: user.user.avatarUrl,
        }}
        roleSelector={
          <FolderPermissionRoleSelector
            currentRole={user.role}
            canManage={canManage}
            onChangeRole={role => onChangeRole(user.user.id, role)}
            getRoleLabel={getRoleLabel}
            sourceType={user.sourceType}
            sourceName={user.sourceName}
            isDirectOverride={user.isDirectOverride}
            onResetDirectOverride={() => onResetRole(user.user.id)}
            workspaceRole={user.workspaceRole}
            isHouseGuest={user.membershipKind === 'house_guests'}
          />
        }
        onRemove={
          canManage && !isOwner && user.isDirectOverride
            ? () => onRemove(user.user.id)
            : undefined
        }
      />
      {areaBlockedNotice ? (
        <div className={accessStyles.areaBlockedNotice}>
          {areaBlockedNotice}
        </div>
      ) : null}
      {canManage && workspaceCeilingNotice ? (
        <div className={accessStyles.areaBlockedNotice}>
          {workspaceCeilingNotice}
        </div>
      ) : null}
    </div>
  );
};

const AccessGroupRow = ({
  gr,
  canManage,
  disabled,
  onGrantGroupRole,
  onRevokeGroupRole,
  onRequestRemove,
  memberWorkspaceRoles,
}: {
  gr: FolderGroupRoleWithName;
  canManage: boolean;
  disabled: boolean;
  onGrantGroupRole: (groupId: string, role: FolderRole) => Promise<boolean>;
  onRevokeGroupRole: (groupId: string) => Promise<boolean>;
  onRequestRemove: (groupId: string) => void;
  memberWorkspaceRoles?: GroupData['memberWorkspaceRoles'];
}) => {
  const t = useI18n();
  const getRoleLabel = useObjectRoleLabel(t);
  const areaBlockedNotice = getAreaBlockedNotice(t, gr);
  const isPartiallyLimited = isFolderGroupRolePartiallyLimited(
    gr.role,
    memberWorkspaceRoles ?? []
  );

  return (
    <div
      className={`${accessStyles.accessRowFrame} ${
        gr.isAreaBlocked ? accessStyles.areaBlockedAccessRow : ''
      }`}
      data-area-blocked={gr.isAreaBlocked ? 'true' : undefined}
      data-folder-access-inherited={gr.isInherited ? 'true' : 'false'}
      data-folder-access-direct={gr.isDirectOverride ? 'true' : 'false'}
    >
      <PermissionGroupRow
        dataTestId={`folder-share-group-row-${gr.groupId}`}
        groupName={gr.groupName}
        roleSlot={
          <FolderPermissionRoleSelector
            currentRole={gr.role}
            canManage={canManage && !disabled}
            onChangeRole={role => {
              onGrantGroupRole(gr.groupId, role).catch(console.error);
            }}
            getRoleLabel={getRoleLabel}
            sourceType={gr.sourceType}
            sourceName={gr.sourceName}
            isDirectOverride={gr.isDirectOverride}
            onResetDirectOverride={() => {
              onRevokeGroupRole(gr.groupId).catch(console.error);
            }}
          />
        }
        onRemove={
          canManage && gr.isDirectOverride
            ? () => onRequestRemove(gr.groupId)
            : undefined
        }
        removeDisabled={disabled}
        removeTestId={`folder-share-remove-group-${gr.groupId}`}
      />
      {areaBlockedNotice ? (
        <div className={accessStyles.areaBlockedNotice}>
          {areaBlockedNotice}
        </div>
      ) : null}
      {isPartiallyLimited ? (
        <div className={accessStyles.areaBlockedNotice}>
          {t['com.streamlain.permissions.group-role-partially-limited']()}
        </div>
      ) : null}
    </div>
  );
};

// --- Groups Section ---

interface GroupsSectionProps {
  folderGroupRoles: FolderGroupRoleWithName[];
  workspaceGroups: GroupData[];
  canManage: boolean;
  disabled: boolean;
  loading: boolean;
  error: boolean;
  onRetry(): void;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  onGrantGroupRole: (groupId: string, role: FolderRole) => Promise<boolean>;
  onRevokeGroupRole: (groupId: string) => Promise<boolean>;
}

const GroupsSection = ({
  folderGroupRoles,
  workspaceGroups,
  canManage,
  disabled,
  loading,
  error,
  onRetry,
  searchText,
  onSearchTextChange,
  onGrantGroupRole,
  onRevokeGroupRole,
}: GroupsSectionProps) => {
  const t = useI18n();
  const inviteCopy = useFolderPermissionInviteCopy('share', 'groups');
  const [confirmRemoveGroup, setConfirmRemoveGroup] = useState<string | null>(
    null
  );
  const roleOptions = useObjectRoleOptions(FOLDER_DIRECT_ASSIGNABLE_ROLES);

  const assignedGroupIds = useMemo(
    () => new Set(folderGroupRoles.map(r => r.groupId)),
    [folderGroupRoles]
  );

  const handleConfirmRemoveGroup = useCallback(async () => {
    if (confirmRemoveGroup) {
      await onRevokeGroupRole(confirmRemoveGroup);
      setConfirmRemoveGroup(null);
    }
  }, [confirmRemoveGroup, onRevokeGroupRole]);

  const groupToRemove = confirmRemoveGroup
    ? folderGroupRoles.find(r => r.groupId === confirmRemoveGroup)
    : null;

  const [groupPage, setGroupPage] = useState({ query: searchText, page: 0 });
  const visibleFolderGroupRoles = useMemo(
    () =>
      folderGroupRoles.filter(group =>
        matchesFolderPermissionQuery(searchText, [group.groupName])
      ),
    [folderGroupRoles, searchText]
  );

  const page = Math.min(
    groupPage.query === searchText ? groupPage.page : 0,
    Math.max(0, Math.ceil(visibleFolderGroupRoles.length / 24) - 1)
  );
  return (
    <>
      <FolderPermissionExpandableSection
        title={t['com.streamlain.folder-share.groups']()}
        defaultCollapsed
      >
        {canManage && (
          <PermissionGroupInviteSearch
            compact
            title={inviteCopy.title}
            placeholder={inviteCopy.placeholder}
            noResultsText={inviteCopy.noResultsText}
            offlineMessage={inviteCopy.offlineMessage}
            roleOptions={roleOptions}
            defaultRole={FolderRoleEnum.Editor}
            groups={workspaceGroups}
            assignedGroupIds={assignedGroupIds}
            disabled={disabled}
            searchText={searchText}
            inputTestId="folder-share-group-search-input"
            onSearchTextChange={onSearchTextChange}
            getGroupDescription={(group, role) =>
              isFolderGroupRolePartiallyLimited(
                role ?? FolderRoleEnum.Editor,
                group.memberWorkspaceRoles ?? []
              )
                ? t['com.streamlain.permissions.group-role-partially-limited']()
                : group.description
            }
            onSelectGroup={(group, role) =>
              onGrantGroupRole(group.id, role ?? FolderRoleEnum.Editor)
            }
          />
        )}

        <PermissionReviewList
          testId="folder-group-review"
          search={searchText}
          onSearchChange={onSearchTextChange}
          hideSearch={canManage}
          placeholder={t['com.streamlain.permissions.review.search-groups']()}
          loading={loading}
          error={error}
          total={
            loading && !visibleFolderGroupRoles.length
              ? null
              : visibleFolderGroupRoles.length
          }
          start={page * 24}
          count={
            visibleFolderGroupRoles.slice(page * 24, (page + 1) * 24).length
          }
          onRetry={onRetry}
          onPrevious={
            page > 0
              ? () => setGroupPage({ query: searchText, page: page - 1 })
              : undefined
          }
          onNext={
            (page + 1) * 24 < visibleFolderGroupRoles.length
              ? () => setGroupPage({ query: searchText, page: page + 1 })
              : undefined
          }
        >
          {visibleFolderGroupRoles.slice(page * 24, (page + 1) * 24).map(gr => (
            <AccessGroupRow
              key={gr.groupId}
              gr={gr}
              canManage={canManage}
              disabled={disabled}
              onGrantGroupRole={onGrantGroupRole}
              onRevokeGroupRole={onRevokeGroupRole}
              onRequestRemove={setConfirmRemoveGroup}
              memberWorkspaceRoles={
                workspaceGroups.find(group => group.id === gr.groupId)
                  ?.memberWorkspaceRoles
              }
            />
          ))}
        </PermissionReviewList>
      </FolderPermissionExpandableSection>

      <ConfirmModal
        open={!!confirmRemoveGroup}
        onOpenChange={(isOpen: boolean) =>
          !isOpen && setConfirmRemoveGroup(null)
        }
        title={t['com.streamlain.folder-share.remove-group-title']()}
        description={t['com.streamlain.folder-share.remove-group-description']({
          name: groupToRemove?.groupName ?? '',
        })}
        confirmText={t['Remove']()}
        confirmButtonOptions={{ variant: 'error' }}
        onConfirm={() => void handleConfirmRemoveGroup()}
      />
    </>
  );
};

// --- Main Modal ---

export const FolderShareModal = ({
  folderId,
  open,
  onOpenChange,
  variant,
}: FolderShareModalProps) => {
  const t = useI18n();
  const getRoleLabel = useObjectRoleLabel(t);
  const peopleInviteCopy = useFolderPermissionInviteCopy('share', 'people');
  const permissionOnlineOnly = useObjectPermissionOnlineOnlyState();
  const {
    folderName,
    owner,
    permissions,
    grantedUsers,
    visibility,
    inheritPermissions,
    isUpdatingInheritance,
    hasParent,
    folderGroupRoles,
    workspaceGroups,
    isLoading,
    refresh,
    isServerConfirmed,
    error,
    grantRole,
    revokeRole,
    setVisibility,
    setInheritPermissions,
    renameFolder,
    grantGroupRole,
    revokeGroupRole,
  } = useFolderShare(folderId);

  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [peopleSearchText, setPeopleSearchText] = useState('');
  const [peoplePage, setPeoplePage] = useState({ query: '', page: 0 });
  const [groupSearchText, setGroupSearchText] = useState('');
  const [folderNameDraft, setFolderNameDraft] = useState(folderName ?? '');

  useEffect(() => {
    if (!open) {
      endObjectConfigurationTrace('folder', 'dialog-closed');
      return;
    }
    const frame = requestAnimationFrame(() => {
      recordObjectConfigurationTraceEvent('shell-visible');
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open || isLoading || error) return;
    const frame = requestAnimationFrame(() => {
      recordObjectConfigurationTraceEvent('data-interactive', {
        serverConfirmed: isServerConfirmed,
      });
      endObjectConfigurationTrace('folder', 'interactive-complete');
    });
    return () => cancelAnimationFrame(frame);
  }, [error, isLoading, isServerConfirmed, open]);

  const controlsLocked =
    isLoading ||
    Boolean(error) ||
    !isServerConfirmed ||
    permissionOnlineOnly.disabled;
  const canManage = permissions?.Folder_Users_Manage ?? false;
  const canUpdate =
    Boolean(permissions?.Folder_Update) &&
    !isLoading &&
    !error &&
    isServerConfirmed &&
    !permissionOnlineOnly.disabled;
  const canManageDirectGrants =
    canManage && (!hasParent || !inheritPermissions);
  const folderRoleOptions = useObjectRoleOptions(
    FOLDER_DIRECT_ASSIGNABLE_ROLES
  );
  const permissionPolicy = useMemo(
    () => getFolderPermissionDialogPolicy(variant),
    [variant]
  );
  const showAdvancedSettings =
    folderPermissionDialogHasAdvancedSettings(permissionPolicy);
  const effectiveVisibility = resolveFolderPermissionVisibility(
    permissionPolicy,
    visibility as FolderPermissionVisibility
  );

  useEffect(() => {
    if (open) {
      setFolderNameDraft(folderName ?? '');
    }
  }, [folderName, open]);

  const handleCommitFolderName = useCallback(
    async (value = folderNameDraft) => {
      if (!canUpdate) {
        return;
      }
      const trimmedName = value.trim();
      const currentName = folderName ?? '';
      if (!trimmedName) {
        setFolderNameDraft(currentName);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.folders.modal.name-required'
            ]?.() ?? 'Please enter a folder name',
        });
        return;
      }
      if (trimmedName === currentName) {
        setFolderNameDraft(currentName);
        return;
      }

      const renamed = await renameFolder(trimmedName);
      if (!renamed) {
        setFolderNameDraft(currentName);
        notify.error({
          title:
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.rename-failed'
            ]?.() ?? 'Folder could not be renamed.',
        });
        return;
      }
      setFolderNameDraft(trimmedName);
    },
    [canUpdate, folderName, folderNameDraft, renameFolder, t]
  );

  const handleChangeRole = useCallback(
    (userId: string, role: FolderRole) => {
      grantRole(userId, role).catch(console.error);
    },
    [grantRole]
  );

  const handleResetRole = useCallback(
    (userId: string) => {
      revokeRole(userId).catch(console.error);
    },
    [revokeRole]
  );

  const handleRemove = useCallback((userId: string) => {
    setConfirmRemove(userId);
  }, []);

  const handleInviteMember = useCallback(
    async (member: Member, role: FolderRole) => {
      if (
        member.membershipKind === 'house_guests' &&
        role !== FolderRoleEnum.Reader &&
        role !== FolderRoleEnum.Editor
      ) {
        notify.error({
          title:
            t['com.streamlain.permissions.role-not-available-for-person'](),
        });
        return;
      }
      const granted = await grantRole(member.id, role, {
        id: member.id,
        name: member.name ?? member.email ?? member.id,
        email: member.email ?? '',
        avatarUrl: member.avatarUrl ?? null,
        workspaceRole: member.permission,
        membershipKind: member.membershipKind,
      });
      if (!granted) {
        throw new Error('Folder role grant failed');
      }
      notify.success({
        title: t['com.streamlain.folder-share.member-added']({
          name: member.name ?? '',
          role: getRoleLabel(role),
        }),
      });
    },
    [getRoleLabel, grantRole, t]
  );

  const handleInviteError = useCallback(() => {
    notify.error({
      title: t['com.streamlain.folder-share.add-failed'](),
    });
  }, [t]);

  const handleConfirmRemove = useCallback(async () => {
    if (confirmRemove) {
      await revokeRole(confirmRemove);
      setConfirmRemove(null);
    }
  }, [confirmRemove, revokeRole]);

  const userToRemove = confirmRemove
    ? grantedUsers.find(u => u.user.id === confirmRemove)
    : null;
  const peoplePresentation = useMemo(
    () =>
      resolvePermissionPeoplePresentation({
        owner,
        users: grantedUsers,
        getOwnerId: currentOwner => currentOwner.id,
        getUserId: user => user.user.id,
        isOwnerUser: user => user.role === FolderRoleEnum.Owner,
        isUserVisible: user =>
          matchesFolderPermissionQuery(peopleSearchText, [
            user.user.name,
            user.user.email,
          ]),
      }),
    [grantedUsers, owner, peopleSearchText]
  );
  const matchingOwner =
    peoplePresentation.owner &&
    matchesFolderPermissionQuery(peopleSearchText, [
      peoplePresentation.owner.name,
      peoplePresentation.owner.email,
    ])
      ? peoplePresentation.owner
      : null;
  const peopleRows = [
    ...(matchingOwner
      ? [{ kind: 'owner' as const, owner: matchingOwner }]
      : []),
    ...peoplePresentation.visibleNonOwners.map(user => ({
      kind: 'user' as const,
      user,
    })),
  ];
  const peopleTotal = peopleRows.length;
  const currentPeoplePage = Math.min(
    peoplePage.query === peopleSearchText ? peoplePage.page : 0,
    Math.max(0, Math.ceil(peopleTotal / 24) - 1)
  );
  const visiblePeople = peopleRows.slice(
    currentPeoplePage * 24,
    (currentPeoplePage + 1) * 24
  );
  const existingUserIds = useMemo(
    () => [
      ...grantedUsers.map(user => user.user.id),
      ...(owner?.id ? [owner.id] : []),
    ],
    [grantedUsers, owner?.id]
  );
  const hasInheritedAccess = useMemo(
    () =>
      grantedUsers.some(user => user.isInherited && !user.isAreaBlocked) ||
      folderGroupRoles.some(group => group.isInherited && !group.isAreaBlocked),
    [folderGroupRoles, grantedUsers]
  );
  const inheritanceSource = useMemo(() => {
    const inheritedEntries = [
      ...grantedUsers.filter(user => !user.isAreaBlocked),
      ...folderGroupRoles.filter(group => !group.isAreaBlocked),
    ].filter(entry => entry.isInherited);
    const sources = new Set(
      inheritedEntries.map(
        entry => `${entry.sourceType}:${entry.sourceName ?? ''}`
      )
    );
    return sources.size === 1 ? inheritedEntries[0] : undefined;
  }, [folderGroupRoles, grantedUsers]);
  const inheritedNote =
    inheritanceSource?.sourceType === 'workspace'
      ? (t['com.streamlain.folder-share.inherited-note.workspace']?.() ??
        'This folder inherits permissions from the Public Area.')
      : inheritanceSource?.sourceType === 'folder' &&
          inheritanceSource.sourceName
        ? (t['com.streamlain.folder-share.inherited-note.folder']?.({
            name: inheritanceSource.sourceName,
          }) ??
          `This folder inherits permissions from "${inheritanceSource.sourceName}".`)
        : t['com.streamlain.folder-share.inherited-note']();
  const showAdvancedSection = showAdvancedSettings || hasInheritedAccess;

  return (
    <>
      <ObjectPermissionDialogShell
        open={open}
        onOpenChange={onOpenChange}
        title={
          folderName
            ? t['com.streamlain.folder-share.title']({ name: folderName })
            : t['com.streamlain.folder-share.title-fallback']()
        }
        confirmButtonOptions={{ style: { display: 'none' } }}
        cancelText={t['com.streamlain.folder-share.close']()}
      >
        <div
          className={styles.container}
          data-testid="folder-share-modal-content"
          data-folder-id={folderId}
          data-folder-share-server-confirmed={
            isServerConfirmed ? 'true' : 'false'
          }
        >
          {isLoading ? (
            <div className={accessStyles.loading} role="status">
              <Loading size={14} />
              {t['com.streamlain.permissions.review.loading']()}
            </div>
          ) : null}
          {error ? (
            <div className={accessStyles.error} role="alert">
              {error}
              <Button variant="plain" onClick={() => void refresh()}>
                {t['com.streamlain.permissions.review.retry']()}
              </Button>
            </div>
          ) : null}
          <>
            <div className={styles.nameSection}>
              <label className={styles.nameLabel}>
                {t[
                  'com.streamlain.settings.workspace.folders.modal.name-label'
                ]()}
              </label>
              <RowInput
                value={folderNameDraft}
                onChange={setFolderNameDraft}
                onEnter={value => void handleCommitFolderName(value)}
                onBlur={() => void handleCommitFolderName()}
                disabled={!canUpdate}
                className={styles.nameInput}
                data-testid="folder-share-name-input"
              />
            </div>

            <FolderPermissionExpandableSection
              title={t['com.streamlain.folder-share.people']()}
              dataTestId="folder-share-people-section"
            >
              {canManageDirectGrants && (
                <PermissionMemberInviteSearch
                  compact
                  title={peopleInviteCopy.title}
                  placeholder={peopleInviteCopy.placeholder}
                  noResultsText={peopleInviteCopy.noResultsText}
                  offlineMessage={peopleInviteCopy.offlineMessage}
                  roleOptions={folderRoleOptions}
                  defaultRole={FolderRoleEnum.Editor}
                  existingUserIds={existingUserIds}
                  disabled={controlsLocked || isUpdatingInheritance}
                  searchText={peopleSearchText}
                  inputTestId="folder-share-people-search-input"
                  onSearchTextChange={setPeopleSearchText}
                  onSelectMember={(member, role) =>
                    handleInviteMember(member, role ?? FolderRoleEnum.Editor)
                  }
                  onSelectError={handleInviteError}
                />
              )}
              <PermissionReviewList
                testId="folder-people-review"
                search={peopleSearchText}
                onSearchChange={setPeopleSearchText}
                hideSearch={canManageDirectGrants}
                placeholder={t['com.streamlain.permissions.review.search']()}
                loading={isLoading}
                error={Boolean(error)}
                onRetry={() => void refresh()}
                total={isLoading && !peopleTotal ? null : peopleTotal}
                start={currentPeoplePage * 24}
                count={visiblePeople.length}
                onPrevious={
                  currentPeoplePage > 0
                    ? () =>
                        setPeoplePage({
                          query: peopleSearchText,
                          page: currentPeoplePage - 1,
                        })
                    : undefined
                }
                onNext={
                  (currentPeoplePage + 1) * 24 < peopleTotal
                    ? () =>
                        setPeoplePage({
                          query: peopleSearchText,
                          page: currentPeoplePage + 1,
                        })
                    : undefined
                }
              >
                {visiblePeople.map(row =>
                  row.kind === 'owner' ? (
                    <div key={row.owner.id}>
                      <PermissionMemberRow
                        user={{
                          ...row.owner,
                          avatarUrl: row.owner.avatarUrl ?? undefined,
                        }}
                        roleSelector={
                          <span className={accessStyles.roleLabel}>
                            {getRoleLabel('Owner')}
                          </span>
                        }
                      />
                      {peoplePresentation.showOwnerDivider &&
                      visiblePeople.length > 1 ? (
                        <div
                          className={accessStyles.accessDivider}
                          role="separator"
                          data-testid="folder-share-owner-divider"
                        />
                      ) : null}
                    </div>
                  ) : (
                    <UserRow
                      key={row.user.user.id}
                      user={row.user}
                      canManage={
                        canManageDirectGrants &&
                        !controlsLocked &&
                        !isUpdatingInheritance
                      }
                      onChangeRole={handleChangeRole}
                      onResetRole={handleResetRole}
                      onRemove={handleRemove}
                      getRoleLabel={getRoleLabel}
                    />
                  )
                )}
              </PermissionReviewList>
            </FolderPermissionExpandableSection>

            <GroupsSection
              loading={isLoading}
              error={Boolean(error)}
              onRetry={() => void refresh()}
              folderGroupRoles={folderGroupRoles}
              workspaceGroups={workspaceGroups}
              canManage={canManageDirectGrants}
              disabled={controlsLocked || isUpdatingInheritance}
              searchText={groupSearchText}
              onSearchTextChange={setGroupSearchText}
              onGrantGroupRole={grantGroupRole}
              onRevokeGroupRole={revokeGroupRole}
            />

            <FolderShareMatesSection
              folderId={folderId}
              canManage={canManageDirectGrants}
              disabled={controlsLocked || isUpdatingInheritance}
            />

            {showAdvancedSection ? (
              <FolderPermissionExpandableSection
                title={t['com.streamlain.quicksearch.advanced.open']()}
                defaultCollapsed
                dataTestId="folder-share-advanced-section"
              >
                <FolderPermissionAdvancedSettings
                  mode="share"
                  policy={permissionPolicy}
                  visibility={effectiveVisibility}
                  inheritPermissions={inheritPermissions}
                  hasParent={hasParent}
                  inheritanceSourceType={
                    inheritanceSource?.sourceType === 'workspace' ||
                    inheritanceSource?.sourceType === 'folder'
                      ? inheritanceSource.sourceType
                      : undefined
                  }
                  inheritanceSourceName={inheritanceSource?.sourceName}
                  disabled={!canUpdate || isUpdatingInheritance}
                  visibilitySwitchTestId="folder-visibility-switch"
                  onSetVisibility={setVisibility}
                  onSetInheritPermissions={setInheritPermissions}
                />

                {hasInheritedAccess && (
                  <div className={styles.inheritedNote}>{inheritedNote}</div>
                )}

                {showAdvancedSettings ? (
                  <div className={styles.footer}>
                    <div className={styles.footerNote}>
                      {t['com.streamlain.folder-share.footer-note']()}
                    </div>
                  </div>
                ) : null}
              </FolderPermissionExpandableSection>
            ) : null}
          </>
        </div>
      </ObjectPermissionDialogShell>

      <ConfirmModal
        open={!!confirmRemove}
        onOpenChange={(isOpen: boolean) => !isOpen && setConfirmRemove(null)}
        title={t['com.streamlain.folder-share.remove-user-title']()}
        description={t['com.streamlain.folder-share.remove-user-description']({
          name: userToRemove?.user.name ?? '',
        })}
        confirmText={t['Remove']()}
        confirmButtonOptions={{ variant: 'error' }}
        onConfirm={() => void handleConfirmRemove()}
      />
    </>
  );
};
